import { useCallback, useMemo, useState } from "react";
import { OrderedMap } from "immutable";
import { useQueryParam, StringParam } from "routing/QueryParams";
import usePaginationQueryParameter, { DEFAULT_PAGE } from "hooks/usePaginationQueryParameter";
import useUrlQueryFilters from "components/common/EntityFilters/hooks/useUrlQueryFilters";
export const useWithURLParams = (layoutConfig, defaultFilters) => {
  const [urlQueryFilters, setUrlQueryFilters] = useUrlQueryFilters();
  const [query, setUrlQuery] = useQueryParam("query", StringParam);
  const [sliceCol, setSliceCol] = useQueryParam("sliceCol", StringParam);
  const [slice, setSlice] = useQueryParam("slice", StringParam);
  const urlPagination = usePaginationQueryParameter(void 0, layoutConfig.pageSize, false);
  const [hasUserModifiedFilters, setHasUserModifiedFilters] = useState(false);
  const effectiveFilters = useMemo(() => {
    if (!hasUserModifiedFilters && urlQueryFilters.isEmpty() && defaultFilters && !defaultFilters.isEmpty()) {
      return defaultFilters;
    }
    return urlQueryFilters;
  }, [hasUserModifiedFilters, urlQueryFilters, defaultFilters]);
  const fetchOptions = useMemo(
    () => ({
      query,
      slice,
      sliceCol,
      page: urlPagination.page,
      pageSize: layoutConfig.pageSize,
      sort: layoutConfig.sort,
      filters: effectiveFilters
    }),
    [query, slice, sliceCol, urlPagination.page, layoutConfig.pageSize, layoutConfig.sort, effectiveFilters]
  );
  const onChangeFilters = useCallback(
    (newUrlQueryFilters) => {
      setHasUserModifiedFilters(true);
      urlPagination.resetPage();
      setUrlQueryFilters(newUrlQueryFilters);
    },
    [urlPagination, setUrlQueryFilters]
  );
  const onChangeSlicing = (newSliceCol, newSlice) => {
    urlPagination.resetPage();
    setSliceCol(newSliceCol);
    setSlice(newSlice);
  };
  return {
    fetchOptions,
    setQuery: setUrlQuery,
    onChangeFilters,
    onChangeSlicing,
    paginationState: urlPagination
  };
};
export const useWithLocalState = (layoutConfig, defaultFilters) => {
  const [transientFetchOptions, setTransientFetchOptions] = useState({
    query: "",
    page: DEFAULT_PAGE,
    filters: defaultFilters != null ? defaultFilters : OrderedMap(),
    slice: void 0,
    sliceCol: void 0
  });
  const setPagination = useCallback(({ page, pageSize }) => {
    setTransientFetchOptions((cur) => ({
      ...cur,
      page,
      pageSize
    }));
  }, []);
  const resetPage = useCallback(() => {
    setTransientFetchOptions((cur) => ({
      ...cur,
      page: DEFAULT_PAGE,
      sort: layoutConfig.sort
    }));
  }, [layoutConfig.sort]);
  const onChangeFilters = useCallback((newFilters) => {
    setTransientFetchOptions((cur) => ({
      ...cur,
      page: DEFAULT_PAGE,
      filters: newFilters
    }));
  }, []);
  const onChangeSlicing = useCallback((sliceCol, slice) => {
    setTransientFetchOptions((cur) => ({
      ...cur,
      page: DEFAULT_PAGE,
      sliceCol,
      slice
    }));
  }, []);
  const setQuery = useCallback((newQuery) => {
    setTransientFetchOptions((cur) => ({
      ...cur,
      query: newQuery,
      page: DEFAULT_PAGE
    }));
  }, []);
  const fetchOptions = useMemo(
    () => ({
      ...transientFetchOptions,
      pageSize: layoutConfig.pageSize,
      sort: layoutConfig.sort
    }),
    [transientFetchOptions, layoutConfig.pageSize, layoutConfig.sort]
  );
  return useMemo(
    () => ({
      fetchOptions,
      setQuery,
      onChangeFilters,
      onChangeSlicing,
      paginationState: {
        page: fetchOptions.page,
        pageSize: fetchOptions.pageSize,
        resetPage,
        setPagination
      }
    }),
    [fetchOptions, onChangeFilters, onChangeSlicing, resetPage, setPagination, setQuery]
  );
};
